import { createFileRoute, useNavigate } from "@tanstack/react-router";
import React from "react";
import { useTranslation } from "react-i18next";

import BackButton from "@/components/back-button";
import { InviteLinkActions } from "@/components/copy-link-button";
import {
  InviteWorkersFields,
  useSubmitWorkerInvites,
} from "@/components/invite-workers-fields";
import type {
  InviteWorkersFieldsHandle,
  SubmitWorkerInvitesResult,
} from "@/components/invite-workers-fields";
import { RoleLink } from "@/components/role-link";
import { useRole } from "@/utils/hooks/use-role";

export const Route = createFileRoute("/app/$role/_app/roster/add")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { onboarding?: boolean } => ({
    onboarding:
      search.onboarding === true || search.onboarding === "true" || undefined,
  }),
  component: AddWorkersPage,
});

/**
 * Bulk-add workers and send them invites. Reached from Your Roster → "Add more
 * workers", and as the last screen of owner onboarding (`?onboarding=true`),
 * where Cancel becomes "Skip for now" and both exits land on the dashboard.
 *
 * Invites are emailed. Workers we couldn't email (phone-only, or the email failed)
 * get a link the owner shares themselves, shown here after sending.
 */
function AddWorkersPage() {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const role = useRole();
  const { onboarding } = Route.useSearch();
  const exitTo = onboarding ? "/app/$role/dashboard" : "/app/$role/roster";
  const fieldsRef = React.useRef<InviteWorkersFieldsHandle>(null);
  const { submit, isPending } = useSubmitWorkerInvites(fieldsRef, {
    allowEmpty: false,
  });
  const [toShare, setToShare] = React.useState<
    SubmitWorkerInvitesResult["toShare"]
  >([]);

  const exit = () =>
    navigate({ to: exitTo, params: { role }, replace: onboarding });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const res = await submit();
    if (!res.ok) return;
    if (res.toShare.length > 0) {
      setToShare(res.toShare);
    } else {
      exit();
    }
  };

  if (toShare.length > 0) {
    return <ShareInviteLinks items={toShare} onDone={exit} />;
  }

  return (
    <div className="flex flex-col w-full px-3 sm:px-6 md:px-8 pt-4 sm:pt-6 md:pt-8 pb-16 max-w-[1200px] mx-auto">
      {!onboarding && <BackButton />}
      <div className="flex flex-col gap-1 mt-4 mb-4">
        <h1 className="heading-1">
          {onboarding
            ? t("v2.roster.add.onboardingTitle")
            : t("v2.roster.add.title")}
        </h1>
        <p className="text-sm text-slate">{t("v2.roster.add.subtitle")}</p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <section className="bg-white rounded-[15px] p-4 sm:p-5">
          <InviteWorkersFields ref={fieldsRef} />
        </section>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:items-center gap-2">
          {onboarding ? (
            <RoleLink
              to="/app/$role/dashboard"
              replace
              className="h-10 px-5 inline-flex items-center justify-center text-sm text-garnet! underline"
            >
              {t("v2.roster.add.skipForNow")}
            </RoleLink>
          ) : (
            <RoleLink
              to="/app/$role/roster"
              className="no-underline! h-10 px-5 inline-flex items-center justify-center rounded-lg border border-fog-dark text-sm text-garnet! hover:bg-fog"
            >
              {t("v2.roster.add.cancel")}
            </RoleLink>
          )}
          <button
            type="submit"
            disabled={isPending}
            className="h-10 px-5 rounded-lg bg-garnet text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {isPending ? t("v2.roster.add.sending") : t("v2.roster.add.submit")}
          </button>
        </div>
      </form>
    </div>
  );
}

/** After sending: links for workers we couldn't email, for the owner to share. */
function ShareInviteLinks({
  items,
  onDone,
}: {
  items: SubmitWorkerInvitesResult["toShare"];
  onDone: () => void;
}) {
  const { t } = useTranslation("dashboard");
  return (
    <div className="flex flex-col w-full px-3 sm:px-6 md:px-8 pt-4 sm:pt-6 md:pt-8 pb-16 max-w-[1200px] mx-auto">
      <div className="flex flex-col gap-1 mt-4 mb-4">
        <h1 className="heading-1">{t("v2.roster.share.title")}</h1>
        <p className="text-sm text-slate">
          {t("v2.roster.share.subtitle", { count: items.length })}
        </p>
      </div>

      <section className="bg-white rounded-[15px] p-4 sm:p-5">
        <ul className="flex flex-col divide-y divide-[#545A63]/15">
          {items.map((it) => (
            <li
              key={it.inviteLink}
              className="py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-garnet">
                  {it.name ?? "—"}
                </div>
                <div className="text-xs text-slate mt-0.5">
                  {[it.phone, it.email].filter(Boolean).join(" · ")}
                  {it.status === "FAILED" &&
                    ` · ${t("v2.roster.share.emailFailed")}`}
                </div>
                <div className="text-xs text-slate mt-1 break-all">
                  {it.inviteLink}
                </div>
              </div>
              <InviteLinkActions
                link={it.inviteLink}
                name={it.name}
                className="self-start sm:self-center"
              />
            </li>
          ))}
        </ul>
        <p className="text-xs text-slate mt-3">{t("v2.roster.share.later")}</p>
      </section>

      <div className="flex justify-end mt-4">
        <button
          type="button"
          onClick={onDone}
          className="h-10 px-5 rounded-lg bg-garnet text-white text-sm font-semibold hover:opacity-90"
        >
          {t("v2.roster.share.done")}
        </button>
      </div>
    </div>
  );
}
